import { ExtendedAccountColumns } from '@/backend/features/contact-management/entities/extended-account/types';
import { ExtendedAccountFilterData } from '@/ui/features/contact-management/AccountsPanel/types';
import { AccountFilterParams, useAccountFilter } from '@juvare/contact-management/ui/features/accounts/hooks/useAccountFilter';
import { useOverrideMapCredentialingAccountFilter } from '@juvare/credentialing/ui/hooks/useOverrideMapCredentialingAccountFilter';
import { Filter } from '@juvare/platform-shared/features/storage';

export const useOverrideAccountFilter = (
    params: AccountFilterParams,
): {
    data?: Filter<ExtendedAccountColumns>[];
    isLoading: boolean;
    error?: unknown;
} => {
    const { data: accountData, error: accountError, isLoading: accountLoading } = useAccountFilter(params);

    const { useOverrideCredentialingAccountFilter } = useOverrideMapCredentialingAccountFilter();
    const {
        data: credentialingData,
        error: credentialingError,
        isLoading: credentialingLoading,
    } = useOverrideCredentialingAccountFilter(params);

    if (accountLoading || credentialingLoading) {
        return { data: undefined, isLoading: true, error: undefined };
    }

    if (accountError || credentialingError) {
        return { data: undefined, isLoading: false, error: accountError || credentialingError };
    }

    let combinedData: Filter<ExtendedAccountColumns>[] = [...((accountData ?? []) as Filter<ExtendedAccountColumns>[])];

    const filterData = params.filterData as ExtendedAccountFilterData;

    if (filterData?.primaryBackgroundCheckType) {
        combinedData.push({
            column: 'primaryBackgroundCheckType',
            op: '=',
            value: filterData.primaryBackgroundCheckType,
        });
    }
    if (filterData?.primaryBackgroundCheckStatus) {
        combinedData.push({
            column: 'primaryBackgroundCheckStatus',
            op: 'in',
            value: filterData.primaryBackgroundCheckStatus,
        });
    }
    if (filterData?.esarVhpId) {
        combinedData.push({ column: 'esarVhpId', op: '=', value: filterData.esarVhpId });
    }
    if (filterData?.stateOfWork) {
        combinedData.push({ column: 'stateOfWork', op: '=', value: filterData.stateOfWork });
    }
    if (filterData?.primaryBackgroundUpdatedAt?.from) {
        combinedData.push({
            column: 'primaryBackgroundUpdatedAt',
            op: '>=',
            value: Date.parse(filterData.primaryBackgroundUpdatedAt.from),
        });
    }
    if (filterData?.primaryBackgroundUpdatedAt?.to) {
        combinedData.push({
            column: 'primaryBackgroundUpdatedAt',
            op: '<',
            value: Date.parse(filterData.primaryBackgroundUpdatedAt.to),
        });
    }

    combinedData = [...combinedData, ...((credentialingData ?? []) as Filter<ExtendedAccountColumns>[])];

    return { data: combinedData, isLoading: false, error: undefined };
};
